import { createFileRoute } from '@tanstack/react-router';
import { EntityOverviewTab } from '@/app/(client)/my-styles/-components/EntityOverviewTab';
import {
  type EntityKindPath,
  isEntityKindPath,
  pathToKind,
  skuDetailQueryOptions,
  styleDetailQueryOptions,
  useEntityDetail,
  variantDetailQueryOptions,
} from '@/app/(client)/my-styles/-hooks/useEntityDetail';
import {
  networkCompanyOptionsQueryOptions,
  styleTypeOptionsQueryOptions,
} from '@/hooks/useDropdowns';
import { stylePropertyCategoryQueryOptions } from '@/hooks/useStylePropertyOptions';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/my-components/(pages)/(detail)/$kind/$id/overview',
)({
  loader: async ({ params }) => {
    if (!isEntityKindPath(params.kind)) return;
    const kind = params.kind;
    const companyId = useSessionStore.getState().user?.companyId ?? '';
    const entityPrefetch =
      kind === 'styles'
        ? queryClient.ensureQueryData(styleDetailQueryOptions(params.id))
        : kind === 'variants'
          ? queryClient.ensureQueryData(variantDetailQueryOptions(params.id))
          : queryClient.ensureQueryData(skuDetailQueryOptions(params.id));
    await Promise.all([
      entityPrefetch,
      queryClient.ensureQueryData(styleTypeOptionsQueryOptions()),
      queryClient.ensureQueryData(networkCompanyOptionsQueryOptions()),
      ...(companyId
        ? (['brands', 'departments', 'seasons', 'collections'] as const).map(
            category =>
              queryClient.ensureQueryData(
                stylePropertyCategoryQueryOptions(category, companyId),
              ),
          )
        : []),
    ]);
  },
  component: EntityOverviewRoute,
});

function EntityOverviewRoute() {
  const { kind, id } = Route.useParams() as {
    kind: EntityKindPath;
    id: string;
  };
  const { data: entity } = useEntityDetail(kind, id);
  if (!entity) return null;
  return <EntityOverviewTab kind={pathToKind(kind)} entity={entity} />;
}
